import { EntitlementSlugEnum } from '@electricjs/core_entity-client';
import { omit } from 'lodash';
import { ComponentType, ReactNode } from 'react';
import styled from 'styled-components';

import { Flex, Tooltip } from '@electricjs/arc';

import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';

import UpgradeChip from './UpgradeChip';
import { UPGRADE_MESSAGE } from '@/constants/packages';

const TopChip = styled(UpgradeChip)`
  position: absolute;
  top: -1.5rem;
  right: 0.5rem;
`;
const BottomChip = styled(UpgradeChip)`
  position: absolute;
  bottom: -1.5rem;
  right: 0.5rem;
`;

const TooltipWrapper = styled.span`
  display: inline-block;
  width: 100%;
`;

// More flexible type that handles both regular React components and styled-components
type UpgradableComponent<P = Record<string, unknown>> =
  | ComponentType<P>
  | React.ForwardRefExoticComponent<P>
  | ((props: P) => React.ReactElement | null);

type UpgradableProps<P> = {
  component: UpgradableComponent<P>;
  chipPosition?: 'inline' | 'top' | 'bottom';
  children: ReactNode;
  entitlement: EntitlementSlugEnum;
} & P;

/**
 * This component wraps a component and adds a chip to it if the user does not have the entitlement.
 * If the user does not have the entitlement, the wrapped component is disabled and a tooltip explains why.
 *
 * Usage:
 * <Upgradable
 *   component={Button}
 *   entitlement={EntitlementSlugEnum.HrisIntegration}>
 *   Do something important
 * </Upgradable>
 */
const Upgradable = <P,>({
  component,
  chipPosition = 'inline',
  children,
  entitlement,
  ...props
}: UpgradableProps<P>) => {
  const entitlementAvailable = useOrganizationHasEntitlement(entitlement);
  const Component = component;

  if (entitlementAvailable) {
    return <Component {...(props as P)}>{children}</Component>;
  }

  const propsWithoutOnClick = omit(props, 'onClick');
  let updatedChildren = children;

  if (chipPosition === 'inline') {
    updatedChildren = (
      <Flex
        flexWrap="nowrap"
        justifyContent="space-between"
        alignItems="center">
        <>{children}</>
        <UpgradeChip />
      </Flex>
    );
  } else if (chipPosition === 'top') {
    updatedChildren = (
      <>
        {children}
        <TopChip />
      </>
    );
  } else {
    updatedChildren = (
      <>
        {children}
        <BottomChip />
      </>
    );
  }

  return (
    <Tooltip placement="top" content={UPGRADE_MESSAGE}>
      <TooltipWrapper>
        <Component disabled {...(propsWithoutOnClick as P)}>
          {updatedChildren}
        </Component>
      </TooltipWrapper>
    </Tooltip>
  );
};

export default Upgradable;
